import {
  Component,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnDestroy,
  SimpleChanges,
} from "@angular/core";
import { AppConfig } from "../../common/app.config";
import { Router } from "@angular/router";
import { LazyLoadEvent } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";
import {
  isGroupedSplitRow,
  isPastProjectRow,
} from "../../../cms/pages/default/project/dashboard/models/dashboard-tabs.rules";

@Component({
  selector: "app-custom-table",
  templateUrl: "./custom-table.component.html",
  styleUrls: ["./custom-table.component.scss"],
})
export class CustomTableComponent implements OnChanges, OnDestroy {
  public showErrMsg = false;
  public validatorModal = false;
  public expensesModal = false;
  public wagesModal = false;
  public today = Date.now();
  public loaderStripesArray = new Array(1);
  public currentLanguage = localStorage.getItem("Language");
  now: Date = new Date();
  private timerId: any;

  public text =
    this.currentLanguage == "de"
      ? "Einträge {first} bis {last} von {totalRecords} Einträgen"
      : this.currentLanguage == "fr"
      ? "entrées {first} à {last} de {totalRecords} entrées"
      : this.currentLanguage == "it"
      ? "voci {first} a {last} da {totalRecords} voci"
      : "Showing {first} to {last} of {totalRecords} entries";

  @Input() tableName: any;
  @Input() orders_budget: any;
  @Input() data: any;
  @Input() columns: any;
  @Input() rowsNumber: any;
  @Input() paginator: any = true;
  @Input() showCurrentPageReport: any;
  @Input() showValidatorConfig: any;
  @Input() requireValidaton: any;
  @Input() numberOfValidators: any;
  @Input() calendarConfig: any;
  @Input() isNotAddable: any;
  @Input() addNewLabel: string = 'addNew';
  @Input() isNotEditable: any;
  @Input() isNotDeletable: any;
  @Input() hasNoActions: any;
  @Input() isAdminAndPMDropdownDisabled: any;
  @Input() isDeactivatable: any;
  @Input() selectedEmployeeIds: any;
  @Input() rowsSelected: any;
  @Input() link: any;
  @Input() customLinkData: string;
  @Input() isBlankTarget: any;
  @Input() noDataText: any;
  @Input() deletionTitle: any;
  @Input() dashboardQueryParams: any;
  @Input() projectTab: any;

  /**
   * JS-1704: the project dashboard can have several tabs selected at once, so `projectTab`
   * is a string there or a list of them. Everything else still passes a single string.
   */
  hasProjectTab(tab: string): boolean {
    return Array.isArray(this.projectTab)
      ? this.projectTab.indexOf(tab) > -1
      : this.projectTab === tab;
  }

  /**
   * JS-1704: the archive action belongs to a past project, not to the Past tab. With one
   * tab selected the two said the same thing; with a combination — Open + Past, say — the
   * tab test put an archive button on every open project in the list too.
   */
  isPastProjectRow(data: any): boolean {
    return this.tableName === 'ProjectsDashboardTable' && isPastProjectRow(data);
  }

  /**
   * JS-1704: in the project dashboard's split view a project with several dates is listed
   * once per date. Those rows carry a bar down their left edge so they read as one project
   * seen several times, rather than as several projects that happen to share a name.
   */
  isGroupedSplitRow(data: any): boolean {
    return this.tableName === 'ProjectsDashboardTable' && isGroupedSplitRow(data);
  }
  @Input() xmlPath: any;
  @Input() isEmployee: any;
  @Input() editingWithIndex: any;
  @Input() isLazyLoaded: any;
  @Input() totalRecords: any;
  @Input() loading: boolean = false;
  @Input() canEditWorkValidation: boolean = true;
  @Input() sortField: string = '';
  @Input() sortOrder: number = 1;
  @Input() showEditRoleAction: boolean = false;

  @Output() add = new EventEmitter<any>();
  @Output() editRole = new EventEmitter<any>();
  @Output() budget = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() reInviteUser = new EventEmitter<any>();
  @Output() openXml = new EventEmitter<any>();
  @Output() blur = new EventEmitter<any>();
  @Output() uploadCalendar = new EventEmitter<any>();
  @Output() editEmployeeHours = new EventEmitter<any>();
  @Output() syncMinBreak = new EventEmitter<any>();
  @Output() updateAdminAndPMRole = new EventEmitter<any>();
  @Output() resendInvitation = new EventEmitter<any>();
  @Output() viewMap = new EventEmitter<any>();
  @Output() deactivate = new EventEmitter<any>();
  @Output() selectDeselectAll = new EventEmitter<any>();
  @Output() checkListChanged = new EventEmitter<any>();
  @Output() checkListChangedNoArg = new EventEmitter<any>();
  @Output() rateEmployee = new EventEmitter<any>();
  // JS-1871: separate emitter for the client-user rating on self-service missions
  // (persisted to client_ratings — distinct from the agency Rating on the same allocation).
  @Output() rateEmployeeAsClient = new EventEmitter<any>();
  @Output() validateTimestamp = new EventEmitter<any>();
  @Output() projectCheckboxChanged = new EventEmitter<any>();
  @Output() exportProjectCostOverview = new EventEmitter<any>();
  @Output() archiveProject = new EventEmitter<any>();
  @Output() discardASP = new EventEmitter<any>();
  @Output() editUnavailability = new EventEmitter<any>();
  @Output() deleteUnavailability = new EventEmitter<any>();
  @Output() acceptUnavailability = new EventEmitter<any>();
  @Output() rejectUnavailability = new EventEmitter<any>();
  @Output() editWithIndex = new EventEmitter<any>();
  @Output() deleteWithIndex = new EventEmitter<any>();
  @Output() lazyLoad = new EventEmitter<any>();
  @Output() clickMessage = new EventEmitter<any>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() addClock = new EventEmitter<any>();
  @Output() showExpensesDetails = new EventEmitter<any>();

  isEditing = false;
  editableBudget: number;
  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableName) {
      if (this.tableName === "SupervisorOnSiteDetailsTable") {
        if (!this.timerId) {
          this.timerId = setInterval(() => {
            this.now = new Date();
            this.cdr.detectChanges();
          }, 5000);
        }
      }
      else {
        if (this.timerId) {
          clearInterval(this.timerId);
          this.timerId = null;
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
  }

  isPaginator(data) {
    return (data?.length > 0 && this.paginator) ? true : false;
  }

  addNewItem() {
    this.add.emit();
  }

  editItem(data) {
    this.edit.emit(data);
  }

  deleteItem(id) {
    this.delete.emit(id);
  }

  reinviteDeletedUser(id){
    this.reInviteUser.emit(id);
  }

  openXML(data) {
    this.openXml.emit(data);
  }

  blurItem(index) {
    this.blur.emit(index);
  }

  onCalendarUpload($event) {
    this.uploadCalendar.emit($event);
  }

  onEditEmployeeHours(id) {
    this.editEmployeeHours.emit(id);
  }

  onSyncMinBreak(index) {
    this.syncMinBreak.emit(index);
  }

  onUpdateAdminAndPMRole(data) {
    this.updateAdminAndPMRole.emit(data);
  }

  onEditRole(data) {
    this.editRole.emit(data);
  }

  onResendInvitation(email) {
    this.resendInvitation.emit(email);
  }

  onViewMap(data) {
    this.viewMap.emit(data);
  }

  onDeactivateEmployee(employeeId, index) {
    this.deactivate.emit({ employeeId, index });
  }

  getThumbnailPath(userId) {
    return AppConfig.getUserPictureThumbnailPath(userId);
  }

  onSelectDeselectAll() {
    this.selectDeselectAll.emit();
  }

  onCheckListChanged(selected, employeeId) {
    this.checkListChanged.emit({ selected, employeeId });
  }

  onCheckListChangedNoArg() {
    this.checkListChangedNoArg.emit();
  }

  onRateEmployee(data) {
    this.rateEmployee.emit(data);
  }

  // JS-1871: separate handler for the client-user rating (self-service missions only).
  onRateEmployeeAsClient(data) {
    this.rateEmployeeAsClient.emit(data);
  }

  onValidateTimestamp(work, valid) {
    this.validateTimestamp.emit({ work, valid });
  }

  roundTotalWage(total) {
    let totalWage = total.split(" ");
    let roundedWage = Number.parseFloat(totalWage[0]).toFixed(2);
    return roundedWage + " " + totalWage[1];
  }

  hasValidExpenses(expenses: any[]): boolean {
    return expenses && expenses.length > 0 && expenses.some(e => !e.is_invalid);
  }

  hasValidWages(wages: any[]): boolean {
    return wages && wages.length > 0 && wages.some(w => !w.is_invalid);
  }

  getShortDescription(desc: string): string {
    if (!desc) return '';
    // JS-1744: descriptions are Quill HTML, so non-breaking spaces are stored as &nbsp;
    // entities. A tag-stripping regex leaves those entities behind as literal text (and the
    // 38-char slice can even cut one in half: "&nbsp"). DOMParser strips the tags AND decodes
    // the entities, without executing scripts or loading resources.
    const text = (new DOMParser().parseFromString(desc, 'text/html').body.textContent || '')
      .replace(/\u00a0/g, ' ');
    let shortDesc = text.slice(0, 38).trim();
    shortDesc = shortDesc.replace(/[\s,\.]+$/, '');
    return shortDesc + (text.length > shortDesc.length ? '…' : '');
  }

  onProjectCheckboxChanged(project, column) {
    console.log(column);
    this.projectCheckboxChanged.emit({ project, column });
  }

  navigateToChat(id) {
    this.router.navigate(["/project/chat/" + id]);
  }

  onExportProjectCostOverview(id) {
    this.exportProjectCostOverview.emit(id);
  }

  onArchiveProject(project, i) {
    this.archiveProject.emit({ project, i });
  }

  onDiscardASP(data) {
    this.discardASP.emit(data);
  }

  onEditUnavailability(data, index) {
    this.editUnavailability.emit({ data, index });
  }

  onDeleteUnavailability(index) {
    this.deleteUnavailability.emit(index);
  }

  onAcceptUnavailability(index) {
    this.acceptUnavailability.emit(index);
  }

  onRejectUnavailability(index) {
    this.rejectUnavailability.emit(index);
  }

  onEditWithIndex(index) {
    this.editWithIndex.emit(index);
  }

  onDeleteWithIndex(index) {
    this.deleteWithIndex.emit(index);
  }

  loadData(event: LazyLoadEvent) {
    this.lazyLoad.emit(event);
  }

  onClickMessage(data) {
    this.clickMessage.emit(data);
  }

  onRowClick(data) {
    this.rowClick.emit(data);
  }

  getLastClockType(employeeTimestamps: any[]): string | null {
    if (!employeeTimestamps || employeeTimestamps.length === 0) {
      return null;
    }
    return employeeTimestamps[employeeTimestamps.length - 1]?.clock_type || null;
  }

  onClock(type: string, data: any) {
    this.addClock.emit({'type': type , 'data': data});
  }

  isYesterday(date: string): boolean {
    const inputDate = new Date(date);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
  
    return (
      inputDate.getDate() === yesterday.getDate() &&
      inputDate.getMonth() === yesterday.getMonth() &&
      inputDate.getFullYear() === yesterday.getFullYear()
    );
  }

  calculateMinutes(timestamps: any[], date: string): number | string {
    const lastClockOut = timestamps.filter((ts: any) => ts.clock_type === 'clock out').sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  
    if (!lastClockOut) {
      return '';
    }
  
    if (!this.isYesterday(date)) {
      return '';
    }
  
    const createdAt = new Date(lastClockOut.created_at).getTime();
    const now = Date.now();
    const diffInMillis = now - createdAt;
    const elapsedMinutes = Math.floor(diffInMillis / (1000 * 60));
    const remainingTime = 15 - elapsedMinutes;

    return remainingTime > 0 ? remainingTime : 0;
  }

  toggleEdit(): void {
    this.isEditing = true;
    this.editableBudget = this.orders_budget;
  }

  saveBudget(): void {
    this.orders_budget = this.editableBudget;
    this.budget.emit(this.editableBudget);
    this.isEditing = false;
    this.cdr.detectChanges();
  }

  generateLink(dataItem: any): string | null {
    if (!this.link) {
      return null;
    }
    if(this.tableName == 'EmployeeJobHistoryTable' || this.tableName == 'FutureJobsTable') {
      return `${this.link}${dataItem.project_id}`;
    } else {
      return `${this.link}${dataItem.id}`;
    }
  }

  generateQueryParams(dataItem: any): any {
    // For WorkValidationTableTable, pass the filter params for flip navigation
    if (this.tableName === 'WorkValidationTableTable' && this.dashboardQueryParams) {
      return this.dashboardQueryParams;
    }
    // Legacy behavior for other tables
    if (this.dashboardQueryParams) {
      return dataItem.id ? {} : { autosaveId: dataItem.autosaveId };
    }
    return null;
  }

  isClockInDisabled(data: { date: string; from: string; employee_timestamps: any[] }): boolean {
    if (data.employee_timestamps.length > 0) {
      return true;
    }
    const start = new Date(`${data.date}T${data.from}`);
    const threshold = start.getTime() - 15 * 60 * 1000;
    return this.now.getTime() < threshold;
  }

  getCurrentLanguageName(field_translations = null, name) {
    if(field_translations) {
      let tranlsations = JSON.parse(field_translations);
      let fieldTranslations = tranlsations.name;
      let language = this.translateService.currentLang;
  
      return fieldTranslations[language] || fieldTranslations["en"] || fieldTranslations["de"] || fieldTranslations["fr"] || fieldTranslations["it"] || name;
    } else {
      return name;
    }
  }

  getCurrentLanguageDescription(field_translations = null, description) {
    if(field_translations) {
      let tranlsations = JSON.parse(field_translations);
      let fieldTranslations = tranlsations.description;
      let language = this.translateService.currentLang;
  
      let desc = fieldTranslations[language] || fieldTranslations["en"] || fieldTranslations["de"] || fieldTranslations["fr"] || fieldTranslations["it"] || description;
      return this.getShortDescription(desc);
    } else {
      return this.getShortDescription(description);
    }
  }

  onShowExpensesDetails(data: any): void {
    this.showExpensesDetails.emit(data);
  }

  formatLocationDistance(metres: number | null): string {
    if (metres == null) return '';
    if (metres < 1000) return `${metres} m`;
    return `${(metres / 1000).toFixed(1)} km`;
  }

  locationAlertTooltip(metres: number | null): string {
    return this.translateService.instant('Clocked in') + ' '
      + this.formatLocationDistance(metres) + ' '
      + this.translateService.instant('from mission location');
  }

  // JS-1741 — merged work-validation alert: true when any timestamp issue (or the GPS alert) is present.
  hasTimestampIssues(data: any): boolean {
    return !!(data && (data.location_alert || data.warning
      || data.incomplete_timestamps || data.min_break_violation || data.no_timestamps));
  }

  timestampIssuesTooltip(data: any): string {
    const lines: string[] = [];
    if (data.location_alert) {
      lines.push(this.locationAlertTooltip(data.location_distance));
    }
    if (data.no_timestamps) {
      lines.push(this.translateService.instant('noTimestampsRecorded'));
    }
    if (data.incomplete_timestamps) {
      lines.push(this.translateService.instant('incompleteTimestamps'));
    }
    if (data.min_break_violation) {
      lines.push(this.translateService.instant('minBreakNotRespected'));
    }
    if (data.warning) {
      lines.push(this.translateService.instant('effectiveHoursDifferFromPlanned'));
    }
    // Joined with <br> so the PrimeNG tooltip ([escape]="false") renders one issue per line.
    return lines.join('<br>');
  }

  getClockInTimestamp(timestamps: any[]): any | null {
    if (!timestamps || !timestamps.length) return null;
    return timestamps.find(t => t.clock_type === 'clock in') || null;
  }

  hasLocationAlert(data: any): boolean {
    const ts = this.getClockInTimestamp(data.employee_timestamps);
    return ts ? !!ts.location_alert : false;
  }

  getActionDashboardClockIn(data: any): any | null {
    return this.getClockInTimestamp(data?.employee_timestamps || data?.employeeTimestamps || []);
  }

  hasActionDashboardLocationAlert(data: any): boolean {
    const clockIn = this.getActionDashboardClockIn(data);
    return !!(data?.location_alert || clockIn?.location_alert);
  }

  getActionDashboardLocationDistance(data: any): number | null {
    if (data?.location_distance !== null && data?.location_distance !== undefined) {
      return data.location_distance;
    }
    const clockIn = this.getActionDashboardClockIn(data);
    return clockIn?.location_distance ?? null;
  }
}
